import { useState, useEffect, useRef } from 'react'
import { useParams, Link } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { cmdbApi, credentialsApi } from '@/api/clients/apiClient'
import { useOrgStore } from '@/stores/orgStore'
import { HandlerModal } from './CmdbHandlers'
import { CreateCredentialModal } from './Credentials'
import type {
  CmdbHandler,
  CmdbSyncStatus,
  CmdbMatchSuggestion,
  FieldDiscoveryResult,
  FileFormat,
  CmdbImportResult,
} from '@/api/types'

// ── Shared UI helpers ─────────────────────────────────────────────────────────

function Card({ children, className = '' }: { children: React.ReactNode; className?: string }) {
  return (
    <div className={`bg-surface-light dark:bg-[#0d1b2a] rounded-2xl shadow-ambient dark:shadow-none border border-border-light dark:border-border-dark p-5 ${className}`}>
      {children}
    </div>
  )
}

function StatBlock({ label, value, sub }: { label: string; value: React.ReactNode; sub?: string }) {
  return (
    <div className="space-y-1">
      <p className="text-xs text-subtle-light dark:text-subtle-dark">{label}</p>
      <p className="text-lg font-semibold text-text-light dark:text-text-dark">{value}</p>
      {sub && <p className="text-xs text-subtle-light dark:text-subtle-dark">{sub}</p>}
    </div>
  )
}

function SyncStatusBadge({ status }: { status: CmdbSyncStatus }) {
  const map: Record<CmdbSyncStatus, string> = {
    synced: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
    pending: 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400',
    failed: 'bg-red-100 text-error dark:bg-red-900/30 dark:text-red-400',
    stale: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400',
  }
  return (
    <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${map[status]}`}>{status}</span>
  )
}

// ── Tab: Overview — Configuration card ───────────────────────────────────────

function ConfigRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-xs text-subtle-light dark:text-subtle-dark">{label}</span>
      <span className="text-sm text-text-light dark:text-text-dark font-medium">{value ?? '—'}</span>
    </div>
  )
}

function nextPollLabel(handler: CmdbHandler): string {
  if (!handler.last_health_check_at) return 'Pending first run'
  const next = new Date(
    new Date(handler.last_health_check_at).getTime() + handler.poll_interval_minutes * 60_000,
  )
  const diff = next.getTime() - Date.now()
  if (diff <= 0) return `${next.toLocaleString()} (overdue)`
  const mins = Math.round(diff / 60_000)
  const rel = mins < 60 ? `in ${mins}m` : `in ${Math.round(mins / 60)}h`
  return `${next.toLocaleString()} (${rel})`
}

function ConfigurationCard({ handler }: { handler: CmdbHandler }) {
  const [expanded, setExpanded] = useState(false)
  const fec = handler.file_exchange_config

  return (
    <Card>
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-sm font-semibold text-text-light dark:text-text-dark">Configuration</h3>
        <button
          onClick={() => setExpanded((v) => !v)}
          className="flex items-center gap-1 text-xs text-subtle-light dark:text-subtle-dark hover:text-primary transition-colors"
        >
          <span className="material-icons text-sm">{expanded ? 'expand_less' : 'expand_more'}</span>
          {expanded ? 'Show less' : 'Show more'}
        </button>
      </div>
      <div className="space-y-5">
        {/* General — always visible */}
        <div>
          <div className="grid grid-cols-2 gap-x-8 gap-y-3">
            <ConfigRow label="Connection Type" value={handler.exchange_type} />
            <ConfigRow label="Direction" value={handler.direction} />
            {handler.exchange_type === 'api' && (
              <>
                <ConfigRow label="Auth Type" value={handler.auth_type ?? '—'} />
                <ConfigRow label="CI Table" value={handler.ci_table} />
                <ConfigRow label="External ID Field" value={handler.external_id_field} />
                {handler.ci_class_filter && (
                  <ConfigRow label="CI Class Filter" value={handler.ci_class_filter} />
                )}
              </>
            )}
          </div>
        </div>

        {expanded && (
          <>
            {/* Polling — inbound only */}
            {handler.direction === 'inbound' && (
              <div>
                <p className="text-xs font-medium text-subtle-light dark:text-subtle-dark uppercase tracking-wide mb-3">Polling</p>
                <div className="grid grid-cols-2 gap-x-8 gap-y-3">
                  <ConfigRow label="Poll Interval" value={`Every ${handler.poll_interval_minutes} min`} />
                  <ConfigRow label="Next Poll" value={nextPollLabel(handler)} />
                  <ConfigRow
                    label="Last Poll"
                    value={
                      handler.last_health_check_at
                        ? new Date(handler.last_health_check_at).toLocaleString()
                        : 'Never'
                    }
                  />
                  <ConfigRow label="Rate Limit" value={`${handler.max_api_calls_per_minute} calls/min`} />
                  <ConfigRow
                    label="Event Subscriptions"
                    value={handler.event_subscription_enabled ? 'Enabled' : 'Disabled'}
                  />
                </div>
              </div>
            )}

            {/* File exchange config */}
            {handler.exchange_type === 'file' && fec && (
              <div>
                <p className="text-xs font-medium text-subtle-light dark:text-subtle-dark uppercase tracking-wide mb-3">File Exchange</p>
                <div className="grid grid-cols-2 gap-x-8 gap-y-3">
                  <ConfigRow label="Transport" value={fec.file_transport} />
                  <ConfigRow label="Format" value={fec.file_format} />
                  {handler.direction === 'inbound' && (
                    <ConfigRow label="Inbound Path" value={fec.inbound_path || '—'} />
                  )}
                  {handler.direction === 'outbound' && (
                    <ConfigRow label="Outbound Path" value={fec.outbound_path || '—'} />
                  )}
                  {handler.direction === 'outbound' && fec.outbound_flush_interval_minutes != null && (
                    <ConfigRow label="Flush Interval" value={`${fec.outbound_flush_interval_minutes} min`} />
                  )}
                  {fec.transfer_timeout_seconds != null && (
                    <ConfigRow label="Transfer Timeout" value={`${fec.transfer_timeout_seconds}s`} />
                  )}
                  <ConfigRow label="Checksum Validation" value={fec.checksum_validation ? 'Yes' : 'No'} />
                  {fec.file_transport === 'sftp' && fec.sftp_host && (
                    <ConfigRow label="SFTP Host" value={`${fec.sftp_host}${fec.sftp_port ? `:${fec.sftp_port}` : ''}`} />
                  )}
                  {fec.file_transport === 'sftp' && fec.sftp_user && (
                    <ConfigRow label="SFTP User" value={fec.sftp_user} />
                  )}
                  {fec.file_transport === 's3' && fec.s3_bucket && (
                    <ConfigRow label="S3 Bucket" value={fec.s3_bucket} />
                  )}
                  {fec.file_transport === 's3' && fec.s3_region && (
                    <ConfigRow label="S3 Region" value={fec.s3_region} />
                  )}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </Card>
  )
}

// ── Tab: Overview ─────────────────────────────────────────────────────────────

function OverviewTab({ orgId, handlerId, handler }: { orgId: string; handlerId: string; handler: CmdbHandler }) {
  const queryClient = useQueryClient()
  const [credError, setCredError] = useState('')
  const [showNewCred, setShowNewCred] = useState(false)
  const [discoverResult, setDiscoverResult] = useState<FieldDiscoveryResult | null>(null)
  const [discoverError, setDiscoverError] = useState('')
  const [downloadingXml, setDownloadingXml] = useState(false)

  const discoverMutation = useMutation({
    mutationFn: () => cmdbApi.discoverFields(orgId, handlerId),
    onSuccess: (r) => { setDiscoverResult(r); setDiscoverError('') },
    onError: (err: Error) => { setDiscoverError(err.message); setDiscoverResult(null) },
  })

  async function handleDownloadUpdateSet() {
    try {
      setDownloadingXml(true)
      const guide = await cmdbApi.getHandlerProvisionGuide(orgId, handlerId)
      if (guide.update_set_xml) {
        const blob = new Blob([guide.update_set_xml], { type: 'application/xml' })
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = 'updateset.xml'
        a.click()
        URL.revokeObjectURL(url)
      }
    } catch {
      // silent — button re-enables
    } finally {
      setDownloadingXml(false)
    }
  }

  const { data: healthResult, error: healthQueryError, isFetching: healthPending, refetch: refetchHealth } = useQuery({
    queryKey: ['cmdb-health', orgId, handlerId],
    queryFn: () => cmdbApi.healthCheck(orgId, handlerId),
    staleTime: Infinity,
    retry: false,
  })

  const { data: credHint } = useQuery({
    queryKey: ['cmdb-credential-hint', orgId, handlerId],
    queryFn: () => cmdbApi.getCredentialHint(orgId, handlerId),
  })

  const { data: existingCreds = [] } = useQuery({
    queryKey: ['credentials', orgId],
    queryFn: () => credentialsApi.list(orgId),
    enabled: !!orgId,
  })

  const currentCredId =
    existingCreds.find(c =>
      c.resources?.some(r => r.type === 'cmdb_handler' && r.id === handlerId)
    )?.id ??
    (credHint?.hint ? existingCreds.find(c => c.hint === credHint.hint)?.id : undefined) ??
    ''

  const [selectedCredId, setSelectedCredId] = useState('')
  const [credSearch, setCredSearch] = useState('')
  const [credDropOpen, setCredDropOpen] = useState(false)
  const credDropRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (currentCredId) setSelectedCredId(currentCredId)
  }, [currentCredId])

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (credDropRef.current && !credDropRef.current.contains(e.target as Node)) {
        setCredDropOpen(false)
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  const filteredCreds = existingCreds.filter(c => {
    const q = credSearch.toLowerCase()
    return c.name.toLowerCase().includes(q) || c.credential_type.toLowerCase().includes(q) || (c.hint ?? '').toLowerCase().includes(q)
  })

  const selectedCred = existingCreds.find(c => c.id === selectedCredId)

  const setCredMutation = useMutation({
    mutationFn: (body: { credential_id: string }) =>
      cmdbApi.setCredential(orgId, handlerId, body as unknown as { credential: string }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['cmdb-credential-hint', orgId, handlerId] })
      queryClient.invalidateQueries({ queryKey: ['credentials', orgId] })
      setCredError('')
    },
    onError: (err: Error) => setCredError(err.message),
  })

  const lastUsedAt =
    credHint?.last_used_at ??
    existingCreds.find(c => c.id === selectedCredId)?.last_used_at

  return (
    <div className="space-y-5">
      <ConfigurationCard handler={handler} />

      {/* Health check */}
      <Card>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold text-text-light dark:text-text-dark">Connection Health</h3>
          <button
            onClick={() => refetchHealth()}
            disabled={healthPending}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-primary/5 text-primary hover:bg-primary/10 disabled:opacity-50 transition-colors"
          >
            <span className={`material-icons text-sm ${healthPending ? 'animate-spin' : ''}`}>
              {healthPending ? 'sync' : 'monitor_heart'}
            </span>
            {healthPending ? 'Checking…' : 'Check Health'}
          </button>
        </div>

        {healthQueryError && (
          <p className="text-xs text-error mb-3">{healthQueryError.message}</p>
        )}

        {healthResult && (
          <div className="space-y-3">
            <div className="flex items-center gap-3">
              <span className={`w-2.5 h-2.5 rounded-full ${healthResult.status === 'healthy' ? 'bg-green-500' : healthResult.status === 'degraded' ? 'bg-yellow-500' : 'bg-error'}`} />
              <span className="text-sm font-medium text-text-light dark:text-text-dark capitalize">{healthResult.status}</span>
              <span className="text-xs text-subtle-light dark:text-subtle-dark">{healthResult.latency_ms}ms</span>
              {healthResult.mode && (
                <span className="text-xs px-2 py-0.5 rounded-full bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400">
                  {healthResult.mode}
                </span>
              )}
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {[
                { label: 'Webhook', val: healthResult.capabilities.supports_webhook },
                { label: 'Writeback', val: healthResult.capabilities.supports_writeback },
                { label: 'Batch', val: healthResult.capabilities.supports_batch },
              ].map(({ label, val }) => (
                <div key={label} className="flex items-center gap-2">
                  <span className={`material-icons text-sm ${val ? 'text-green-500' : 'text-subtle-light dark:text-subtle-dark'}`}>
                    {val ? 'check_circle' : 'cancel'}
                  </span>
                  <span className="text-xs text-text-light dark:text-text-dark">{label}</span>
                </div>
              ))}
              <div>
                <span className="text-xs text-subtle-light dark:text-subtle-dark">API Version: </span>
                <span className="text-xs text-text-light dark:text-text-dark">{healthResult.capabilities.api_version || '—'}</span>
              </div>
            </div>
            <p className="text-xs text-subtle-light dark:text-subtle-dark">
              Checked {new Date(healthResult.checked_at).toLocaleString()}
            </p>
          </div>
        )}
      </Card>
      {/* Credential */}
      <Card>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-sm font-semibold text-text-light dark:text-text-dark">Credential</h3>
          <button
            onClick={() => setShowNewCred(true)}
            className="flex items-center gap-1 text-xs text-primary hover:text-primary/80 transition-colors"
          >
            <span className="material-icons text-sm">add</span>
            New Credential
          </button>
        </div>

        {existingCreds.length === 0 ? (
          <p className="text-xs text-subtle-light dark:text-subtle-dark py-1">
            No credentials yet.{' '}
            <button onClick={() => setShowNewCred(true)} className="text-primary hover:underline">
              Create one now.
            </button>
          </p>
        ) : (
          <div ref={credDropRef} className="relative">
            <div
              className={`w-full flex items-center gap-2 pl-3 pr-3 py-2 text-sm rounded-lg border border-border-light dark:border-border-dark bg-surface-light dark:bg-[#0d1b2a] cursor-pointer ${setCredMutation.isPending ? 'opacity-60 pointer-events-none' : ''}`}
              onClick={() => { setCredDropOpen(o => !o); setCredSearch('') }}
            >
              {credDropOpen ? (
                <input
                  autoFocus
                  value={credSearch}
                  onChange={(e) => setCredSearch(e.target.value)}
                  onKeyDown={(e) => e.key === 'Escape' && setCredDropOpen(false)}
                  placeholder="Search credentials…"
                  className="flex-1 bg-transparent outline-none focus:outline-none focus:ring-0 focus:border-none border-none ring-0 p-0 m-0 text-sm text-text-light dark:text-text-dark placeholder:text-subtle-light dark:placeholder:text-subtle-dark"
                  onClick={(e) => e.stopPropagation()}
                />
              ) : (
                <span className={`flex-1 truncate ${selectedCred ? 'text-text-light dark:text-text-dark' : 'text-subtle-light dark:text-subtle-dark'}`}>
                  {selectedCred ? `${selectedCred.name} · ${selectedCred.credential_type}${selectedCred.hint ? ` (${selectedCred.hint})` : ''}` : '— select credential —'}
                </span>
              )}
              <span className="material-icons text-sm text-subtle-light dark:text-subtle-dark">
                {setCredMutation.isPending ? 'sync' : credDropOpen ? 'expand_less' : 'expand_more'}
              </span>
            </div>
            {credDropOpen && (
              <div className="absolute z-10 mt-1 w-full bg-surface-light dark:bg-[#0d1b2a] border border-border-light dark:border-border-dark rounded-lg shadow-float dark:shadow-none max-h-48 overflow-y-auto">
                {filteredCreds.length === 0 ? (
                  <p className="px-3 py-2 text-sm text-subtle-light dark:text-subtle-dark">No matches</p>
                ) : filteredCreds.map(c => (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => {
                      setSelectedCredId(c.id)
                      setCredDropOpen(false)
                      setCredMutation.mutate({ credential_id: c.id })
                    }}
                    className={`w-full text-left px-3 py-2 text-sm hover:bg-background-light dark:hover:bg-background-dark transition-colors ${c.id === selectedCredId ? 'text-primary font-medium' : 'text-text-light dark:text-text-dark'}`}
                  >
                    <span className="font-medium">{c.name}</span>
                    <span className="text-subtle-light dark:text-subtle-dark ml-1 font-mono text-xs">· {c.credential_type}{c.hint ? ` (${c.hint})` : ''}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
        {lastUsedAt && (
          <p className="text-xs text-subtle-light dark:text-subtle-dark mt-2">
            Last used {new Date(lastUsedAt).toLocaleString()}
          </p>
        )}
        {credError && <p className="text-xs text-error mt-2">{credError}</p>}
      </Card>

      {showNewCred && (
        <CreateCredentialModal
          orgId={orgId}
          onClose={() => setShowNewCred(false)}
          onCreated={(id) => {
            setShowNewCred(false)
            setSelectedCredId(id)
            setCredMutation.mutate({ credential_id: id })
          }}
        />
      )}

      {/* Webhook inbound URL */}
      {handler.exchange_type !== 'file' && (
        <Card>
          <h3 className="text-sm font-semibold text-text-light dark:text-text-dark mb-4">Webhook Inbound URL</h3>
          <p className="text-xs text-subtle-light dark:text-subtle-dark mb-3">
            Configure your CMDB to POST events to this endpoint. Requests must include the{' '}
            <span className="font-mono">X-CMDB-Signature</span> HMAC header.
          </p>
          <div className="flex gap-2">
            <input
              readOnly
              value={`${window.location.origin}/api/v1/cmdb/webhooks/${handlerId}`}
              className="flex-1 px-3 py-2 text-xs font-mono rounded-lg border border-border-light dark:border-border-dark bg-surface-light dark:bg-[#0d1b2a] text-text-light dark:text-text-dark"
            />
            <button
              onClick={() => navigator.clipboard.writeText(`${window.location.origin}/api/v1/cmdb/webhooks/${handlerId}`)}
              className="px-3 py-2 text-sm font-medium rounded-lg bg-primary/5 text-primary hover:bg-primary/10 transition-colors"
              title="Copy URL"
            >
              <span className="material-icons text-sm">content_copy</span>
            </button>
          </div>
        </Card>
      )}

      {/* Field discovery */}
      {handler.exchange_type !== 'file' && (
        <Card>
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-sm font-semibold text-text-light dark:text-text-dark">Field Discovery</h3>
            <button
              onClick={() => discoverMutation.mutate()}
              disabled={discoverMutation.isPending}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-primary/5 text-primary hover:bg-primary/10 disabled:opacity-50 transition-colors"
            >
              <span className={`material-icons text-sm ${discoverMutation.isPending ? 'animate-spin' : ''}`}>
                {discoverMutation.isPending ? 'sync' : 'search'}
              </span>
              {discoverMutation.isPending ? 'Discovering…' : 'Discover Fields'}
            </button>
          </div>
          {discoverError && <p className="text-xs text-error mb-3">{discoverError}</p>}
          {discoverResult ? (
            <div className="space-y-3">
              <p className="text-xs text-subtle-light dark:text-subtle-dark">
                {discoverResult.message} · {discoverResult.record_count} records sampled
              </p>
              <div className="overflow-x-auto">
                <table className="w-full text-xs">
                  <thead>
                    <tr className="border-b border-border-light dark:border-border-dark">
                      <th className="text-left pb-2 text-subtle-light dark:text-subtle-dark font-medium">Field</th>
                      <th className="text-left pb-2 text-subtle-light dark:text-subtle-dark font-medium">Sample</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border-light dark:divide-border-dark">
                    {discoverResult.fields.map((f) => (
                      <tr key={f.name}>
                        <td className="py-1.5 pr-4 font-mono text-text-light dark:text-text-dark">{f.name}</td>
                        <td className="py-1.5 text-subtle-light dark:text-subtle-dark truncate max-w-xs">{f.sample ?? '—'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            !discoverError && (
              <p className="text-xs text-subtle-light dark:text-subtle-dark">
                Sample live fields from your CMDB to help configure field mappings.
              </p>
            )
          )}
        </Card>
      )}

      {/* ServiceNow Update Set XML */}
      {handler.exchange_type === 'api' && (
        <Card>
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="text-sm font-semibold text-text-light dark:text-text-dark">ServiceNow Provisioning</h3>
              <p className="text-xs text-subtle-light dark:text-subtle-dark mt-1">
                Download an Update Set XML to create the required <span className="font-mono">u_pqc_*</span> fields in your ServiceNow instance.
              </p>
            </div>
            <button
              onClick={handleDownloadUpdateSet}
              disabled={downloadingXml}
              className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-primary/5 text-primary hover:bg-primary/10 disabled:opacity-50 transition-colors"
            >
              <span className={`material-icons text-sm ${downloadingXml ? 'animate-spin' : ''}`}>
                {downloadingXml ? 'sync' : 'download'}
              </span>
              {downloadingXml ? 'Generating…' : 'Download Update Set XML'}
            </button>
          </div>
        </Card>
      )}
    </div>
  )
}

// ── Tab: Field Mappings ───────────────────────────────────────────────────────

function FieldMappingsTab({ orgId, handler }: { orgId: string; handler: CmdbHandler }) {
  const { data: profile, isLoading } = useQuery({
    queryKey: ['cmdb-profile', orgId, handler.profile_id],
    queryFn: () => cmdbApi.getProfile(orgId, handler.profile_id!),
    enabled: !!handler.profile_id,
  })

  if (!handler.profile_id) {
    return (
      <div className="py-10 text-center">
        <p className="text-sm text-subtle-light dark:text-subtle-dark">No mapping profile assigned. Edit the handler to assign one.</p>
      </div>
    )
  }

  if (isLoading) {
    return <div className="flex justify-center py-10"><span className="material-icons animate-spin text-subtle-light dark:text-subtle-dark">sync</span></div>
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <p className="text-sm text-subtle-light dark:text-subtle-dark">{profile?.fields.length ?? 0} mappings from profile</p>
        <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-secondary/20 text-secondary-dark dark:text-secondary">{profile?.name}</span>
        {profile?.is_system && (
          <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400">system</span>
        )}
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="border-b border-border-light dark:border-border-dark">
              {['QTZ Field', 'CMDB Field', 'Active'].map((h) => (
                <th key={h} className="text-left py-2 pr-3 text-subtle-light dark:text-subtle-dark font-medium">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {(profile?.fields ?? []).map((f) => (
              <tr key={f.id ?? f.qtz_field} className="border-b border-border-light dark:border-border-dark last:border-0">
                <td className="py-2 pr-3 font-mono text-text-light dark:text-text-dark">{f.qtz_field}</td>
                <td className="py-2 pr-3 font-mono text-text-light dark:text-text-dark">{f.cmdb_field}</td>
                <td className="py-2">
                  <span className={`material-icons text-sm ${f.is_active !== false ? 'text-green-500' : 'text-subtle-light dark:text-subtle-dark'}`}>
                    {f.is_active !== false ? 'check_circle' : 'cancel'}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  )
}

// ── Tab: Sync Status ──────────────────────────────────────────────────────────

function SyncStatusTab({ orgId, handlerId }: { orgId: string; handlerId: string }) {
  const queryClient = useQueryClient()
  const [statusFilter, setStatusFilter] = useState<CmdbSyncStatus | ''>('')
  const [driftOnly, setDriftOnly] = useState(false)

  const { data: resp, isLoading } = useQuery({
    queryKey: ['cmdb-sync-status', orgId, handlerId, statusFilter, driftOnly],
    queryFn: () =>
      cmdbApi.getSyncStatus(orgId, handlerId, {
        sync_status: statusFilter || undefined,
        drift_flag: driftOnly || undefined,
        page_size: 50,
      }),
  })

  const fullSyncMutation = useMutation({
    mutationFn: () => cmdbApi.triggerFullSync(orgId, handlerId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['cmdb-sync-status', orgId, handlerId] }),
  })

  const invSyncMutation = useMutation({
    mutationFn: (inventoryId: string) => cmdbApi.triggerInventorySync(orgId, handlerId, inventoryId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['cmdb-sync-status', orgId, handlerId] }),
  })

  const items = resp?.items ?? []
  const total = resp?.total ?? 0

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as CmdbSyncStatus | '')}
            className="pl-3 pr-7 py-1.5 text-xs rounded-lg border border-border-light dark:border-border-dark bg-surface-light dark:bg-[#0d1b2a] text-text-light dark:text-text-dark focus:outline-none focus:ring-2 focus:ring-primary/40"
          >
            <option value="">All statuses</option>
            <option value="synced">Synced</option>
            <option value="pending">Pending</option>
            <option value="failed">Failed</option>
            <option value="stale">Stale</option>
          </select>
          <label className="flex items-center gap-1.5 text-xs text-text-light dark:text-text-dark cursor-pointer">
            <input
              type="checkbox"
              checked={driftOnly}
              onChange={(e) => setDriftOnly(e.target.checked)}
              className="w-3.5 h-3.5 rounded border border-border-light dark:border-border-dark accent-primary dark:accent-secondary bg-white dark:bg-[#0d1b2a]"
            />
            Drift only
          </label>
          <span className="text-xs text-subtle-light dark:text-subtle-dark">{total} total</span>
        </div>
        <button
          onClick={() => fullSyncMutation.mutate()}
          disabled={fullSyncMutation.isPending}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-primary text-white hover:bg-primary/90 disabled:opacity-50 transition-colors"
        >
          <span className={`material-icons text-sm ${fullSyncMutation.isPending ? 'animate-spin' : ''}`}>sync</span>
          {fullSyncMutation.isPending ? 'Syncing…' : 'Full Sync'}
        </button>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-10">
          <span className="material-icons animate-spin text-subtle-light dark:text-subtle-dark">sync</span>
        </div>
      ) : items.length === 0 ? (
        <p className="text-sm text-subtle-light dark:text-subtle-dark py-8 text-center">No sync records found.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-border-light dark:border-border-dark">
                {['Inventory', 'CMDB Ref', 'Status / Reason', 'Drift', 'Last Outbound', 'Last Inbound', 'Retries', ''].map((h) => (
                  <th key={h} className="text-left py-2 pr-3 font-medium text-subtle-light dark:text-subtle-dark last:text-right">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {items.map((s) => (
                <tr key={s.id} className="border-b border-border-light dark:border-border-dark last:border-0 hover:bg-primary/2">
                  <td className="py-2 pr-3">
                    <p className="font-medium text-text-light dark:text-text-dark">{s.inventory_name ?? s.inventory_id.slice(0, 8)}</p>
                    <p className="text-subtle-light dark:text-subtle-dark font-mono text-[10px]">{s.inventory_id.slice(0, 8)}</p>
                  </td>
                  <td className="py-2 pr-3 font-mono text-text-light dark:text-text-dark">{s.cmdb_ref}</td>
                  <td className="py-2 pr-3">
                    <SyncStatusBadge status={s.sync_status} />
                    {s.sync_status === 'failed' && s.failure_reason && (
                      <p className="text-[10px] text-error mt-0.5 max-w-[180px] truncate" title={s.failure_reason}>
                        {s.failure_reason}
                      </p>
                    )}
                  </td>
                  <td className="py-2 pr-3">
                    {s.drift_flag && (
                      <span className="material-icons text-yellow-500 text-sm" title="Drift detected">warning</span>
                    )}
                  </td>
                  <td className="py-2 pr-3 text-subtle-light dark:text-subtle-dark">
                    {s.last_outbound_sync_at ? new Date(s.last_outbound_sync_at).toLocaleString() : '—'}
                  </td>
                  <td className="py-2 pr-3 text-subtle-light dark:text-subtle-dark">
                    {s.last_inbound_sync_at ? new Date(s.last_inbound_sync_at).toLocaleString() : '—'}
                  </td>
                  <td className="py-2 pr-3 text-text-light dark:text-text-dark">{s.retry_count}</td>
                  <td className="py-2 text-right">
                    <button
                      onClick={() => invSyncMutation.mutate(s.inventory_id)}
                      disabled={invSyncMutation.isPending}
                      className="p-1 rounded hover:bg-primary/5 text-subtle-light dark:text-subtle-dark hover:text-primary transition-colors"
                      title="Re-sync this inventory"
                    >
                      <span className="material-icons text-sm">sync</span>
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

// ── Tab: Risk Summary ─────────────────────────────────────────────────────────

function RiskSummaryTab({ orgId, handlerId }: { orgId: string; handlerId: string }) {
  const { data: rs, isLoading, refetch } = useQuery({
    queryKey: ['cmdb-risk-summary', orgId, handlerId],
    queryFn: () => cmdbApi.getRiskSummary(orgId, handlerId),
  })

  if (isLoading) {
    return <div className="flex justify-center py-10"><span className="material-icons animate-spin text-subtle-light dark:text-subtle-dark">sync</span></div>
  }

  if (!rs) return <p className="text-sm text-subtle-light dark:text-subtle-dark py-8 text-center">No risk summary available.</p>

  const riskLevelColors: Record<string, string> = {
    CRITICAL: 'text-error',
    HIGH: 'text-orange-500',
    MEDIUM: 'text-yellow-500',
    LOW: 'text-green-500',
    NONE: 'text-subtle-light dark:text-subtle-dark',
  }

  // risk_distribution is level → env → count; pivot to env rows
  const LEVELS = ['CRITICAL', 'HIGH', 'MEDIUM', 'LOW', 'NONE']
  const envs = [...new Set(Object.values(rs.risk_distribution ?? {}).flatMap(Object.keys))]

  // migration_bands is band → env → count; sum across envs for display
  const bandTotals = Object.entries(rs.migration_bands ?? {}).map(([band, envCounts]) => ({
    band,
    total: Object.values(envCounts).reduce((a, b) => a + b, 0),
  }))

  return (
    <div className="space-y-5">
      <div className="flex justify-end">
        <button onClick={() => refetch()} className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-primary/5 text-primary hover:bg-primary/10 transition-colors">
          <span className="material-icons text-sm">refresh</span>
          Refresh
        </button>
      </div>

      {rs.is_stale && (
        <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-yellow-50 dark:bg-yellow-900/10 border border-yellow-200 dark:border-yellow-800">
          <span className="material-icons text-yellow-500 text-sm">warning</span>
          <p className="text-xs text-yellow-700 dark:text-yellow-400">Risk summary may be stale — data was computed before the latest sync.</p>
        </div>
      )}

      {/* Coverage + trend */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Card>
          <StatBlock
            label="Write-back Coverage"
            value={`${(rs.write_back_coverage_pct ?? 0).toFixed(1)}%`}
            sub="inventories synced"
          />
        </Card>
        <Card>
          <StatBlock
            label="Orphaned CIs"
            value={rs.orphaned_ci_count}
            sub="no matching inventory"
          />
        </Card>
        <Card>
          <StatBlock
            label="Overdue Deadlines"
            value={rs.overdue_deadline_total}
            sub="inventories past deadline"
          />
        </Card>
        <Card>
          <StatBlock
            label="Computed At"
            value={new Date(rs.computed_at).toLocaleDateString()}
            sub={new Date(rs.computed_at).toLocaleTimeString()}
          />
        </Card>
      </div>

      {/* Risk distribution — rows=env, cols=level */}
      {envs.length > 0 && (
        <Card>
          <h3 className="text-sm font-semibold text-text-light dark:text-text-dark mb-4">Risk Distribution by Environment</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="border-b border-border-light dark:border-border-dark">
                  <th className="text-left py-2 pr-3 font-medium text-subtle-light dark:text-subtle-dark">Environment</th>
                  {LEVELS.map((l) => (
                    <th key={l} className={`text-right py-2 pr-3 font-medium ${riskLevelColors[l]}`}>{l}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {envs.map((env) => (
                  <tr key={env} className="border-b border-border-light dark:border-border-dark last:border-0">
                    <td className="py-2 pr-3 font-medium text-text-light dark:text-text-dark capitalize">{env}</td>
                    {LEVELS.map((l) => {
                      const count = rs.risk_distribution[l]?.[env] ?? 0
                      return (
                        <td key={l} className={`py-2 pr-3 text-right font-medium ${count ? riskLevelColors[l] : 'text-subtle-light dark:text-subtle-dark'}`}>
                          {count}
                        </td>
                      )
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Migration bands */}
      {bandTotals.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">
          {bandTotals.map(({ band, total }) => (
            <Card key={band}>
              <StatBlock label={band.charAt(0).toUpperCase() + band.slice(1)} value={total} />
            </Card>
          ))}
        </div>
      )}

      {/* Risk trend + compliance */}
      <div className="grid sm:grid-cols-2 gap-4">
        <Card>
          <h3 className="text-sm font-semibold text-text-light dark:text-text-dark mb-3">Risk Trend</h3>
          <div className="space-y-2">
            {Object.entries(rs.risk_trend ?? {}).map(([trend, count]) => {
              const colors: Record<string, string> = {
                improving: 'text-green-500', stable: 'text-primary', degrading: 'text-error',
              }
              return (
                <div key={trend} className="flex items-center justify-between">
                  <span className={`text-sm capitalize ${colors[trend] ?? ''}`}>{trend}</span>
                  <span className="text-sm font-semibold text-text-light dark:text-text-dark">{count}</span>
                </div>
              )
            })}
          </div>
        </Card>

        <Card>
          <h3 className="text-sm font-semibold text-text-light dark:text-text-dark mb-3">PQC Compliance</h3>
          <div className="space-y-2">
            {Object.entries(rs.compliant_count ?? {}).map(([k, v]) => (
              <div key={k} className="flex items-center justify-between">
                <span className="text-sm text-text-light dark:text-text-dark capitalize">{k === 'true' ? 'Compliant' : 'Non-compliant'}</span>
                <span className="text-sm font-semibold text-text-light dark:text-text-dark">{v}</span>
              </div>
            ))}
          </div>
        </Card>
      </div>

      {/* Stale data */}
      <Card>
        <h3 className="text-sm font-semibold text-text-light dark:text-text-dark mb-3">Stale Data</h3>
        <div className="grid grid-cols-3 gap-4">
          {[
            { label: '>30 days', val: rs.stale_data?.['gt_30_days'] ?? 0 },
            { label: '>60 days', val: rs.stale_data?.['gt_60_days'] ?? 0 },
            { label: '>90 days', val: rs.stale_data?.['gt_90_days'] ?? 0 },
          ].map(({ label, val }) => (
            <div key={label}>
              <p className="text-xs text-subtle-light dark:text-subtle-dark">{label}</p>
              <p className={`text-lg font-semibold ${val > 0 ? 'text-error' : 'text-text-light dark:text-text-dark'}`}>{val}</p>
            </div>
          ))}
        </div>
      </Card>
    </div>
  )
}

// ── Tab: Match Wizard ─────────────────────────────────────────────────────────

function MatchWizardTab({ orgId, handlerId }: { orgId: string; handlerId: string }) {
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [confirmError, setConfirmError] = useState('')
  const [confirmed, setConfirmed] = useState(false)

  const { data: resp, isLoading, refetch } = useQuery({
    queryKey: ['cmdb-match-suggestions', orgId, handlerId],
    queryFn: () => cmdbApi.getMatchSuggestions(orgId, handlerId),
  })

  const confirmMutation = useMutation({
    mutationFn: (matches: CmdbMatchSuggestion[]) =>
      cmdbApi.confirmMatches(orgId, handlerId, {
        matches: matches.map((m) => ({ inventory_id: m.inventory_id, cmdb_ref: m.suggested_cmdb_ref })),
      }),
    onSuccess: () => { setConfirmed(true); setConfirmError(''); refetch() },
    onError: (err: Error) => setConfirmError(err.message),
  })

  const suggestions = resp ?? []
  const selectedSuggestions = suggestions.filter((s) => selected.has(s.inventory_id))

  const toggleAll = () => {
    if (selected.size === suggestions.length) {
      setSelected(new Set())
    } else {
      setSelected(new Set(suggestions.map((s) => s.inventory_id)))
    }
  }

  if (isLoading) {
    return <div className="flex justify-center py-10"><span className="material-icons animate-spin text-subtle-light dark:text-subtle-dark">sync</span></div>
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <p className="text-sm text-text-light dark:text-text-dark font-medium">CMDBRef Auto-Match</p>
          <p className="text-xs text-subtle-light dark:text-subtle-dark mt-0.5">
            Fuzzy-match inventories to CMDB CIs. Confirm matches before any write.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => refetch()} className="px-3 py-1.5 text-xs font-medium rounded-lg text-subtle-light dark:text-subtle-dark hover:bg-primary/5 transition-colors">
            <span className="material-icons text-sm">refresh</span>
          </button>
          <button
            onClick={() => confirmMutation.mutate(selectedSuggestions)}
            disabled={selected.size === 0 || confirmMutation.isPending}
            className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg bg-primary text-white hover:bg-primary/90 disabled:opacity-50 transition-colors"
          >
            <span className="material-icons text-sm">check_circle</span>
            {confirmMutation.isPending ? 'Confirming…' : `Confirm ${selected.size > 0 ? `(${selected.size})` : ''}`}
          </button>
        </div>
      </div>

      {confirmed && (
        <div className="rounded-lg bg-green-50 dark:bg-green-900/10 border border-green-200 dark:border-green-800 p-3">
          <p className="text-xs text-green-700 dark:text-green-400">Matches confirmed and applied.</p>
        </div>
      )}
      {confirmError && <p className="text-xs text-error">{confirmError}</p>}

      {suggestions.length === 0 ? (
        <p className="text-sm text-subtle-light dark:text-subtle-dark py-8 text-center">
          No unmatched inventories or no CI suggestions available.
        </p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-border-light dark:border-border-dark">
                <th className="py-2 pr-3">
                  <input
                    type="checkbox"
                    checked={selected.size === suggestions.length}
                    onChange={toggleAll}
                    className="w-3.5 h-3.5 rounded border border-border-light dark:border-border-dark accent-primary dark:accent-secondary bg-white dark:bg-[#0d1b2a]"
                  />
                </th>
                {['Inventory', 'Suggested CI', 'CMDB Ref', 'Confidence', 'Auto-approvable'].map((h) => (
                  <th key={h} className="text-left py-2 pr-3 font-medium text-subtle-light dark:text-subtle-dark">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {suggestions.map((s) => (
                <tr key={s.inventory_id} className={`border-b border-border-light dark:border-border-dark last:border-0 ${selected.has(s.inventory_id) ? 'bg-primary/3' : ''}`}>
                  <td className="py-2 pr-3">
                    <input
                      type="checkbox"
                      checked={selected.has(s.inventory_id)}
                      onChange={() => {
                        setSelected((prev) => {
                          const next = new Set(prev)
                          if (next.has(s.inventory_id)) { next.delete(s.inventory_id) } else { next.add(s.inventory_id) }
                          return next
                        })
                      }}
                      className="w-3.5 h-3.5 rounded border border-border-light dark:border-border-dark accent-primary dark:accent-secondary bg-white dark:bg-[#0d1b2a]"
                    />
                  </td>
                  <td className="py-2 pr-3 text-text-light dark:text-text-dark font-medium">{s.inventory_name}</td>
                  <td className="py-2 pr-3 text-text-light dark:text-text-dark">{s.suggested_ci_name}</td>
                  <td className="py-2 pr-3 font-mono text-subtle-light dark:text-subtle-dark">{s.suggested_cmdb_ref}</td>
                  <td className="py-2 pr-3">
                    <div className="flex items-center gap-2">
                      <div className="w-16 h-1.5 rounded-full bg-border-light dark:bg-border-dark overflow-hidden">
                        <div
                          className={`h-full rounded-full ${s.confidence >= 90 ? 'bg-green-500' : s.confidence >= 70 ? 'bg-yellow-500' : 'bg-orange-500'}`}
                          style={{ width: `${s.confidence}%` }}
                        />
                      </div>
                      <span className={`font-medium ${s.confidence >= 90 ? 'text-green-500' : s.confidence >= 70 ? 'text-yellow-500' : 'text-orange-500'}`}>
                        {s.confidence}%
                      </span>
                    </div>
                  </td>
                  <td className="py-2 pr-3">
                    {s.auto_approvable
                      ? <span className="material-icons text-green-500 text-sm">check_circle</span>
                      : <span className="material-icons text-subtle-light dark:text-subtle-dark text-sm">radio_button_unchecked</span>
                    }
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

// ── Tab: Import / Export ──────────────────────────────────────────────────────

const _DEFAULT_INV_COLS = [
  'external_id', 'inventory_name', 'environment', 'criticality',
  'technology_owner', 'business_owner', 'security_owner', 'business_unit', 'department',
  'lifecycle_stage', 'data_classification', 'tags',
]
const _SOURCE_COLS = [
  'source_type', 'source_url', 'source_name', 'inventory_external_id', 'owner_email', 'lifecycle_status',
]

function _downloadBlob(content: string, filename: string, mime: string) {
  const blob = new Blob([content], { type: mime })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url; a.download = filename; a.click()
  URL.revokeObjectURL(url)
}

function _downloadCsvTemplate(cols: string[]) {
  const allCols = ['record_type', ...cols, ..._SOURCE_COLS]
  const invRow = ['inventory', ...cols.map((_, i) => i === 0 ? 'ci-001' : ''), ..._SOURCE_COLS.map(() => '')]
  const srcRow = ['source', ...cols.map(() => ''), 'git', 'https://github.com/org/repo', 'repo-name', 'ci-001', 'owner@example.com', 'active']
  const csv = [allCols, invRow, srcRow].map(r => r.map(v => v.includes(',') ? `"${v}"` : v).join(',')).join('\n')
  _downloadBlob(csv, 'cmdb_import_template.csv', 'text/csv')
}

function _downloadJsonTemplate(cols: string[]) {
  const tpl = {
    schema_version: '2.0', import_type: 'inbound', import_mode: 'upsert',
    inventories: [Object.fromEntries(cols.map((c, i) => [c, i === 0 ? 'ci-001' : '']))],
    sources: [{ source_type: 'git', source_url: 'https://github.com/org/repo', source_name: 'repo-name', inventory_external_id: 'ci-001', owner_email: 'owner@example.com', lifecycle_status: 'active' }],
  }
  _downloadBlob(JSON.stringify(tpl, null, 2), 'cmdb_import_template.json', 'application/json')
}

function ImportResultCard({ result }: { result: CmdbImportResult }) {
  return (
    <div className={`rounded-xl p-4 border space-y-1 ${(result.errors ?? []).length > 0 ? 'bg-yellow-50 dark:bg-yellow-900/10 border-yellow-200 dark:border-yellow-800' : 'bg-green-50 dark:bg-green-900/10 border-green-200 dark:border-green-800'}`}>
      <p className="text-sm font-medium text-text-light dark:text-text-dark">
        {result.inventories_processed} inventories · {result.sources_processed} sources processed
        {result.inventories_created != null && (
          <span className="ml-2 text-xs text-subtle-light dark:text-subtle-dark">
            ({result.inventories_created} created, {result.inventories_updated ?? 0} updated)
          </span>
        )}
      </p>
      {(result.errors ?? []).map((e, i) => <p key={i} className="text-xs text-error">{e}</p>)}
    </div>
  )
}

function ImportExportTab({ orgId, handler }: { orgId: string; handler: CmdbHandler }) {
  const [subTab, setSubTab] = useState<'import' | 'export'>(handler.direction === 'inbound' ? 'import' : 'export')
  const [profileId] = useState(handler.profile_id ?? '')
  const queryClient = useQueryClient()
  const { data: profile } = useQuery({
    queryKey: ['cmdb-profile', orgId, handler.profile_id],
    queryFn: () => cmdbApi.getProfile(orgId, handler.profile_id!),
    enabled: !!handler.profile_id,
  })
  const [format, setFormat] = useState<FileFormat>('csv')
  const [method, setMethod] = useState<'file' | 'json'>('file')
  const [file, setFile] = useState<File | null>(null)
  const [jsonBody, setJsonBody] = useState('')
  const [fileResult, setFileResult] = useState<CmdbImportResult | null>(null)
  const [jsonResult, setJsonResult] = useState<CmdbImportResult | null>(null)
  const [fileError, setFileError] = useState('')
  const [jsonError, setJsonError] = useState('')

  const invalidateAfterImport = () => {
    queryClient.invalidateQueries({ queryKey: ['inventories', orgId] })
    queryClient.invalidateQueries({ queryKey: ['sources', orgId] })
  }

  const fileMutation = useMutation({
    mutationFn: (f: File) => cmdbApi.standaloneImportFile(orgId, f, profileId || undefined),
    onSuccess: (r) => { setFileResult(r); setFileError(''); invalidateAfterImport() },
    onError: (err: Error) => { setFileError(err.message); setFileResult(null) },
  })

  const jsonMutation = useMutation({
    mutationFn: (body: unknown) => cmdbApi.standaloneImportJson(orgId, body),
    onSuccess: (r) => { setJsonResult(r); setJsonError(''); invalidateAfterImport() },
    onError: (err: Error) => { setJsonError(err.message); setJsonResult(null) },
  })

  const downloadMutation = useMutation({
    mutationFn: () => cmdbApi.standaloneDownloadExport(orgId, format, profileId || undefined),
  })

  const handleJsonImport = () => {
    try {
      const parsed = JSON.parse(jsonBody) as Record<string, unknown>
      if (profileId) parsed.mapping_profile_id = profileId
      parsed.import_mode = 'upsert'
      jsonMutation.mutate(parsed)
    } catch {
      setJsonError('Invalid JSON — check syntax and try again.')
    }
  }

  const templateCols = _DEFAULT_INV_COLS

  return (
    <div className="space-y-4">
      {/* Sub-tab toggle */}
      <div className="flex rounded-xl overflow-hidden w-fit">
        {(['import', 'export'] as const).map((t) => (
          <button key={t} onClick={() => setSubTab(t)} className={`px-5 py-2 text-sm font-medium transition-colors capitalize ${subTab === t ? 'bg-primary text-white' : 'text-subtle-light dark:text-subtle-dark hover:bg-primary/5'}`}>
            {t}
          </button>
        ))}
      </div>

      {/* Profile badge */}
      {profileId && (
        <div className="flex items-center gap-2 text-xs text-subtle-light dark:text-subtle-dark">
          <span className="material-icons text-sm">account_tree</span>
          Using handler profile
          <span className="px-2 py-0.5 rounded-full bg-secondary/20 text-secondary-dark dark:text-secondary font-medium">
            {profile?.name ?? profileId.slice(0, 8) + '…'}
          </span>
          {profile?.is_system && (
            <span className="px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400">system</span>
          )}
        </div>
      )}

      {/* Import */}
      {subTab === 'import' && (
        <div className="space-y-4">
          {/* Template download */}
          <Card>
            <h3 className="text-sm font-semibold text-text-light dark:text-text-dark mb-1">Download Template</h3>
            <p className="text-xs text-subtle-light dark:text-subtle-dark mb-3">
              Use as a starting point. Mix inventory and source rows — the <code className="font-mono">record_type</code> column distinguishes them.
            </p>
            <div className="flex items-center gap-2">
              <button onClick={() => _downloadCsvTemplate(templateCols)} className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium rounded-lg text-text-light dark:text-text-dark hover:bg-primary/5 transition-colors">
                <span className="material-icons text-sm">download</span>CSV Template
              </button>
              <button onClick={() => _downloadJsonTemplate(templateCols)} className="flex items-center gap-1.5 px-3 py-2 text-xs font-medium rounded-lg text-text-light dark:text-text-dark hover:bg-primary/5 transition-colors">
                <span className="material-icons text-sm">download</span>JSON Template
              </button>
            </div>
          </Card>

          {/* Method toggle */}
          <div className="flex rounded-xl overflow-hidden w-fit">
            {(['file', 'json'] as const).map((m) => (
              <button key={m} onClick={() => setMethod(m)} className={`px-4 py-2 text-xs font-medium transition-colors ${method === m ? 'bg-primary text-white' : 'text-subtle-light dark:text-subtle-dark hover:bg-primary/5'}`}>
                {m === 'file' ? 'File Upload' : 'JSON Paste'}
              </button>
            ))}
          </div>

          {/* File upload */}
          {method === 'file' && (
            <Card>
              <h3 className="text-sm font-semibold text-text-light dark:text-text-dark mb-4">Upload File</h3>
              <div className="space-y-3">
                <div>
                  <label className="block text-xs font-medium text-subtle-light dark:text-subtle-dark mb-1">File (CSV / JSON / XML / XLS / XLSX)</label>
                  <input type="file" accept=".csv,.json,.xml,.xls,.xlsx" onChange={(e) => { setFile(e.target.files?.[0] ?? null); setFileResult(null) }} className="block w-full text-xs text-subtle-light dark:text-subtle-dark file:mr-3 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:text-xs file:font-medium file:bg-primary/5 file:text-primary hover:file:bg-primary/10 cursor-pointer" />
                </div>
                <button onClick={() => file && fileMutation.mutate(file)} disabled={!file || fileMutation.isPending} className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-lg bg-primary text-white hover:bg-primary/90 disabled:opacity-50 transition-colors">
                  <span className={`material-icons text-sm ${fileMutation.isPending ? 'animate-spin' : ''}`}>{fileMutation.isPending ? 'sync' : 'upload_file'}</span>
                  {fileMutation.isPending ? 'Importing…' : 'Import File'}
                </button>
                {fileError && <p className="text-xs text-error">{fileError}</p>}
                {fileResult && <ImportResultCard result={fileResult} />}
              </div>
            </Card>
          )}

          {/* JSON paste */}
          {method === 'json' && (
            <Card>
              <h3 className="text-sm font-semibold text-text-light dark:text-text-dark mb-1">Paste JSON Payload</h3>
              <p className="text-xs text-subtle-light dark:text-subtle-dark mb-4">Paste a <code className="font-mono">PQCImportEnvelope</code> with <code className="font-mono">inventories[]</code> and <code className="font-mono">sources[]</code>.</p>
              <div className="space-y-3">
                <textarea value={jsonBody} onChange={(e) => { setJsonBody(e.target.value); setJsonResult(null) }} rows={14} placeholder={'{\n  "schema_version": "2.0",\n  "import_type": "inbound",\n  "inventories": [...],\n  "sources": [...]\n}'} className="w-full px-3 py-2 text-xs font-mono rounded-lg border border-border-light dark:border-border-dark bg-surface-light dark:bg-[#0d1b2a] text-text-light dark:text-text-dark placeholder:text-subtle-light dark:placeholder:text-subtle-dark focus:outline-none focus:ring-2 focus:ring-primary/40 resize-none" />
                <div className="flex items-center gap-2">
                  <button onClick={() => { setJsonBody(JSON.stringify({ schema_version: '2.0', import_type: 'inbound', inventories: [], sources: [] }, null, 2)); setJsonResult(null) }} className="px-3 py-1.5 text-xs font-medium rounded-lg text-subtle-light dark:text-subtle-dark hover:bg-primary/5 transition-colors">Reset to template</button>
                  <button onClick={handleJsonImport} disabled={!jsonBody.trim() || jsonMutation.isPending} className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-lg bg-primary text-white hover:bg-primary/90 disabled:opacity-50 transition-colors">
                    <span className={`material-icons text-sm ${jsonMutation.isPending ? 'animate-spin' : ''}`}>{jsonMutation.isPending ? 'sync' : 'data_object'}</span>
                    {jsonMutation.isPending ? 'Importing…' : 'Import JSON'}
                  </button>
                </div>
                {jsonError && <p className="text-xs text-error">{jsonError}</p>}
                {jsonResult && <ImportResultCard result={jsonResult} />}
              </div>
            </Card>
          )}
        </div>
      )}

      {/* Export */}
      {subTab === 'export' && (
        <Card>
          <h3 className="text-sm font-semibold text-text-light dark:text-text-dark mb-4">Download Export</h3>
          <div className="mb-4 max-w-xs">
            <label className="block text-xs font-medium text-subtle-light dark:text-subtle-dark mb-1">Format</label>
            <select value={format} onChange={(e) => setFormat(e.target.value as FileFormat)} className="w-full px-3 py-2 text-sm rounded-lg border border-border-light dark:border-border-dark bg-surface-light dark:bg-[#0d1b2a] text-text-light dark:text-text-dark focus:outline-none focus:ring-2 focus:ring-primary/40">
              {(['csv', 'json', 'xml', 'xls', 'xlsx'] as FileFormat[]).map((f) => (
                <option key={f} value={f}>{f.toUpperCase()}</option>
              ))}
            </select>
          </div>
          <button onClick={() => downloadMutation.mutate()} disabled={downloadMutation.isPending} className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-lg bg-primary text-white hover:bg-primary/90 disabled:opacity-50 transition-colors">
            <span className="material-icons text-sm">download</span>
            {downloadMutation.isPending ? 'Downloading…' : `Download ${format.toUpperCase()}`}
          </button>
        </Card>
      )}
    </div>
  )
}

// ── CmdbHandlerDetail (page) ────────────────────────────────────────────────

const TABS = [
  { key: 'overview', label: 'Overview', icon: 'dashboard' },
  { key: 'mappings', label: 'Field Mappings', icon: 'swap_horiz' },
  { key: 'sync', label: 'Sync Status', icon: 'sync' },
  { key: 'risk', label: 'Risk Summary', icon: 'bar_chart' },
  { key: 'wizard', label: 'Match Wizard', icon: 'auto_fix_high' },
  { key: 'importexport', label: 'Import / Export', icon: 'import_export' },
] as const

type TabKey = (typeof TABS)[number]['key']

export default function CmdbHandlerDetail() {
  const { id: handlerId } = useParams<{ id: string }>()
  const { currentOrgId: orgId } = useOrgStore()
  const [activeTab, setActiveTab] = useState<TabKey>('overview')
  const [editing, setEditing] = useState(false)

  const queryClient = useQueryClient()

  const { data: handler, isLoading } = useQuery({
    queryKey: ['cmdb-handler', orgId, handlerId],
    queryFn: () => cmdbApi.getHandler(orgId, handlerId!),
    enabled: !!orgId && !!handlerId,
  })

  const activateMutation = useMutation({
    mutationFn: () => cmdbApi.activateHandler(orgId, handlerId!),
    onSuccess: (updated) => {
      queryClient.setQueryData(['cmdb-handler', orgId, handlerId], updated)
      queryClient.invalidateQueries({ queryKey: ['cmdb-handlers', orgId] })
    },
  })

  const deactivateMutation = useMutation({
    mutationFn: () => cmdbApi.deactivateHandler(orgId, handlerId!),
    onSuccess: (updated) => {
      queryClient.setQueryData(['cmdb-handler', orgId, handlerId], updated)
      queryClient.invalidateQueries({ queryKey: ['cmdb-handlers', orgId] })
    },
  })

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-24">
        <span className="material-icons text-subtle-light dark:text-subtle-dark animate-spin text-3xl">sync</span>
      </div>
    )
  }

  if (!handler) {
    return (
      <div className="p-6 text-center">
        <p className="text-sm text-subtle-light dark:text-subtle-dark">Handler not found.</p>
        <Link to="/cmdb" className="text-xs text-primary hover:underline mt-2 block">← Back to handlers</Link>
      </div>
    )
  }

  const statusColors: Record<string, string> = {
    active: 'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-400',
    inactive: 'bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-400',
    error: 'bg-red-100 text-error dark:bg-red-900/30 dark:text-red-400',
  }

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-6">
      {/* Breadcrumb + header */}
      <div className="space-y-3">
        <Link to="/cmdb" className="flex items-center gap-1 text-xs text-subtle-light dark:text-subtle-dark hover:text-primary transition-colors">
          <span className="material-icons text-sm">arrow_back</span>
          CMDB Handlers
        </Link>
        <div className="flex items-center gap-3 flex-wrap">
          <h1 className="text-xl font-semibold text-text-light dark:text-text-dark">{handler.name}</h1>
          <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
            handler.exchange_type === 'file'
              ? 'bg-yellow-100 text-yellow-700 dark:bg-yellow-900/30 dark:text-yellow-400'
              : 'bg-primary/10 text-primary'
          }`}>
            {handler.exchange_type}
          </span>
          <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${statusColors[handler.status]}`}>
            {handler.status}
          </span>
          <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-secondary/15 text-secondary-dark dark:text-secondary">
            {handler.direction}
          </span>
          <button
            onClick={() => setEditing(true)}
            className="flex items-center gap-1 text-xs px-2.5 py-1 rounded-lg text-subtle-light dark:text-subtle-dark hover:text-primary hover:border-primary transition-colors"
          >
            <span className="material-icons text-sm">edit</span>
            Edit
          </button>
          {handler.status === 'active' ? (
            <button
              onClick={() => deactivateMutation.mutate()}
              disabled={deactivateMutation.isPending}
              className="flex items-center gap-1 text-xs px-2.5 py-1 rounded-lg text-subtle-light dark:text-subtle-dark hover:text-error hover:border-error transition-colors disabled:opacity-50"
            >
              <span className="material-icons text-sm">pause_circle</span>
              Deactivate
            </button>
          ) : (
            <button
              onClick={() => activateMutation.mutate()}
              disabled={activateMutation.isPending}
              className="flex items-center gap-1 text-xs px-2.5 py-1 rounded-lg text-subtle-light dark:text-subtle-dark hover:text-green-600 hover:border-green-600 transition-colors disabled:opacity-50"
            >
              <span className="material-icons text-sm">play_circle</span>
              Activate
            </button>
          )}
        </div>
        {handler.instance_url && (
          <p className="text-xs text-subtle-light dark:text-subtle-dark">{handler.instance_url}</p>
        )}
      </div>

      {/* Tabs */}
      <div className="border-b border-border-light dark:border-border-dark">
        <nav className="flex gap-1 overflow-x-auto pb-px">
          {TABS.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              className={`flex items-center gap-1.5 px-3 py-2.5 text-xs font-medium whitespace-nowrap border-b-2 transition-colors ${
                activeTab === tab.key
                  ? 'border-primary text-primary'
                  : 'border-transparent text-subtle-light dark:text-subtle-dark hover:text-text-light dark:hover:text-text-dark'
              }`}
            >
              <span className="material-icons text-sm">{tab.icon}</span>
              {tab.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab content */}
      <div>
        {activeTab === 'overview' && <OverviewTab orgId={orgId} handlerId={handlerId!} handler={handler} />}
        {activeTab === 'mappings' && <FieldMappingsTab orgId={orgId} handler={handler} />}
        {activeTab === 'sync' && <SyncStatusTab orgId={orgId} handlerId={handlerId!} />}
        {activeTab === 'risk' && <RiskSummaryTab orgId={orgId} handlerId={handlerId!} />}
        {activeTab === 'wizard' && <MatchWizardTab orgId={orgId} handlerId={handlerId!} />}
        {activeTab === 'importexport' && <ImportExportTab orgId={orgId} handler={handler} />}
      </div>

      {editing && (
        <HandlerModal
          handler={handler}
          onClose={() => {
            setEditing(false)
            queryClient.invalidateQueries({ queryKey: ['cmdb-handler', orgId, handlerId] })
          }}
        />
      )}
    </div>
  )
}
